// TOTGA Advanced Features
(function() {
  'use strict';

  // ==========================================
  // SCROLL REVEAL ANIMATIONS
  // ==========================================
  class ScrollReveal {
    constructor() {
      this.elements = document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right');
      this.init();
    }

    init() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('revealed');
              this.observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        });

        this.elements.forEach(el => this.observer.observe(el));
      } else {
        // Fallback for older browsers
        this.elements.forEach(el => el.classList.add('revealed'));
      }
    }
  }

  // ==========================================
  // PARALLAX EFFECT
  // ==========================================
  class ParallaxEffect {
    constructor() {
      this.elements = document.querySelectorAll('[data-parallax]');
      this.init();
    }

    init() {
      if (this.elements.length === 0) return;

      window.addEventListener('scroll', () => {
        requestAnimationFrame(() => this.update());
      }, { passive: true });

      this.update();
    }

    update() {
      const scrollY = window.pageYOffset;

      this.elements.forEach(el => {
        const speed = parseFloat(el.dataset.parallax) || 0.5;
        const rect = el.getBoundingClientRect();
        const elementY = rect.top + scrollY;
        const diff = scrollY - elementY;
        const translate = diff * speed;

        el.style.transform = `translate3d(0, ${translate}px, 0)`;
      });
    }
  }

  // ==========================================
  // SCROLL PROGRESS BAR
  // ==========================================
  class ScrollProgress {
    constructor() {
      this.createProgressBar();
      this.init();
    }

    createProgressBar() {
      this.bar = document.createElement('div');
      this.bar.className = 'scroll-progress';
      document.body.appendChild(this.bar);
    }

    init() {
      window.addEventListener('scroll', () => {
        requestAnimationFrame(() => this.update());
      }, { passive: true });

      this.update();
    }

    update() {
      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      this.bar.style.width = scrolled + '%';
    }
  }

  // ==========================================
  // BACK TO TOP BUTTON
  // ==========================================
  class BackToTop {
    constructor() {
      this.createButton();
      this.init();
    }

    createButton() {
      this.button = document.createElement('button');
      this.button.className = 'back-to-top';
      this.button.setAttribute('aria-label', 'Back to top');
      this.button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
      `;
      document.body.appendChild(this.button);

      this.addStyles();
    }

    init() {
      window.addEventListener('scroll', () => {
        if (window.pageYOffset > 500) {
          this.button.classList.add('visible');
        } else {
          this.button.classList.remove('visible');
        }
      }, { passive: true });

      this.button.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    addStyles() {
      const style = document.createElement('style');
      style.textContent = `
        .back-to-top {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background-color: var(--color-accent);
          border: none;
          cursor: pointer;
          opacity: 0;
          visibility: hidden;
          transform: translateY(20px) scale(0.8);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .back-to-top.visible {
          opacity: 1;
          visibility: visible;
          transform: translateY(0) scale(1);
        }

        .back-to-top:hover {
          transform: translateY(-4px) scale(1.05);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
        }

        .back-to-top:active {
          transform: translateY(-2px) scale(0.95);
        }

        .back-to-top svg {
          width: 24px;
          height: 24px;
          stroke: white;
        }

        @media screen and (max-width: 768px) {
          .back-to-top {
            bottom: 20px;
            right: 20px;
            width: 44px;
            height: 44px;
          }
        }

        /* Sit above fixed Pick & Mix / Lead Mix bars */
        body.has-mix-sticky-bar .back-to-top {
          bottom: 96px;
        }

        @media screen and (max-width: 600px) {
          body.has-mix-sticky-bar .back-to-top {
            bottom: calc(168px + env(safe-area-inset-bottom, 0px));
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // ==========================================
  // RECENTLY VIEWED PRODUCTS
  // ==========================================
  class RecentlyViewed {
    constructor() {
      this.storageKey = 'totga_recently_viewed';
      this.maxItems = 8;
    }

    add(productData) {
      let items = this.getAll();
      
      // Remove if already exists
      items = items.filter(item => item.id !== productData.id);
      
      // Add to beginning
      items.unshift({
        ...productData,
        viewedAt: Date.now()
      });

      // Keep only max items
      items = items.slice(0, this.maxItems);

      try {
        localStorage.setItem(this.storageKey, JSON.stringify(items));
      } catch (e) {
        console.error('Failed to save recently viewed:', e);
      }
    }

    getAll() {
      try {
        const stored = localStorage.getItem(this.storageKey);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        return [];
      }
    }

    clear() {
      localStorage.removeItem(this.storageKey);
    }
  }

  // ==========================================
  // STICKY ADD TO CART BAR (Product Page)
  // ==========================================
  class StickyAddToCart {
    constructor() {
      if (!document.querySelector('.product-single')) return;
      
      this.createBar();
      this.init();
    }

    createBar() {
      const product = document.querySelector('.product-single');
      if (!product) return;

      const title = (product.querySelector('.product-title')?.textContent || '').replace(/\s+/g, ' ').trim();
      const price = (product.querySelector('.product-price')?.textContent || '').replace(/\s+/g, ' ').trim();
      const image = product.querySelector('.product-main-image img')?.src || '';
      const mainBtn = product.querySelector('.button-add-to-cart');
      const btnLabel = (mainBtn?.textContent || 'Add to Cart').replace(/\s+/g, ' ').trim();
      const isDisabled = !!(mainBtn && mainBtn.disabled);

      this.bar = document.createElement('div');
      this.bar.className = 'sticky-add-to-cart';
      this.bar.innerHTML = `
        <div class="sticky-cart-content page-width">
          <div class="sticky-cart-product">
            <img src="${image}" alt="${title}" class="sticky-cart-image">
            <div class="sticky-cart-info">
              <h3 class="sticky-cart-title">${title}</h3>
              <p class="sticky-cart-price">${price}</p>
            </div>
          </div>
          <button type="button" class="button button-primary sticky-cart-button"${isDisabled ? ' disabled' : ''}>
            ${btnLabel}
          </button>
        </div>
      `;
      document.body.appendChild(this.bar);

      this.stickyButton = this.bar.querySelector('.sticky-cart-button');
      this.stickyPrice = this.bar.querySelector('.sticky-cart-price');

      this.stickyButton.addEventListener('click', () => {
        if (this.stickyButton.disabled) return;
        document.querySelector('.button-add-to-cart')?.click();
      });

      this.addStyles();
    }

    syncFromMainButton() {
      const mainBtn = document.querySelector('.button-add-to-cart');
      if (!mainBtn || !this.stickyButton) return;

      const label = (mainBtn.textContent || '').replace(/\s+/g, ' ').trim();
      if (label) this.stickyButton.textContent = label;
      this.stickyButton.disabled = mainBtn.disabled;

      const price = document.querySelector('.product-single .product-price');
      if (price && this.stickyPrice) {
        this.stickyPrice.textContent = price.textContent.replace(/\s+/g, ' ').trim();
      }
    }

    init() {
      const addToCartButton = document.querySelector('.button-add-to-cart');
      if (!addToCartButton || !this.bar) return;

      this.syncFromMainButton();

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.bar.classList.remove('visible');
            document.body.classList.remove('has-sticky-cart');
          } else {
            this.syncFromMainButton();
            this.bar.classList.add('visible');
            document.body.classList.add('has-sticky-cart');
          }
        });
      }, { threshold: 0 });

      observer.observe(addToCartButton);

      /* Keep sticky label/disabled in sync if the main button changes */
      const btnObserver = new MutationObserver(() => this.syncFromMainButton());
      btnObserver.observe(addToCartButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    addStyles() {
      const style = document.createElement('style');
      style.textContent = `
        .sticky-add-to-cart {
          position: fixed;
          left: 16px;
          right: 16px;
          bottom: 16px;
          background-color: rgba(18, 18, 18, 0.96);
          border: 1px solid var(--color-border);
          border-radius: 12px;
          padding: 12px 0;
          z-index: 1100;
          transform: translateY(calc(100% + 24px));
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
          backdrop-filter: blur(6px);
        }

        .sticky-add-to-cart.visible {
          transform: translateY(0);
        }

        /* Keep below the cart drawer checkout panel */
        body:has(.cart-drawer.is-open) .sticky-add-to-cart {
          transform: translateY(calc(100% + 24px));
          pointer-events: none;
        }

        .sticky-cart-content {
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 20px;
        }

        .sticky-cart-product {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .sticky-cart-image {
          width: 52px;
          height: 52px;
          object-fit: cover;
          border-radius: var(--radius-sm);
          flex-shrink: 0;
        }

        .sticky-cart-title {
          font-size: 0.9375rem;
          font-weight: 600;
          margin: 0 0 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sticky-cart-price {
          font-size: 0.9375rem;
          font-weight: 700;
          color: var(--color-text);
          margin: 0;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sticky-cart-button {
          white-space: nowrap;
          min-width: 190px;
          padding: 12px 28px;
          font-size: 0.875rem;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .sticky-cart-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        body.has-sticky-cart .back-to-top {
          bottom: 108px;
        }

        @media screen and (max-width: 768px) {
          .sticky-add-to-cart {
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 0;
            border-left: 0;
            border-right: 0;
            padding: 10px 0 calc(10px + env(safe-area-inset-bottom));
            backdrop-filter: none;
          }

          .sticky-cart-content {
            gap: 8px;
            grid-template-columns: 1fr;
            align-items: stretch;
          }

          .sticky-cart-product {
            gap: 10px;
            min-width: 0;
            width: 100%;
          }

          .sticky-cart-image {
            width: 40px;
            height: 40px;
          }

          .sticky-cart-info {
            display: block;
            min-width: 0;
          }

          .sticky-cart-title {
            font-size: 0.8125rem;
            margin-bottom: 2px;
          }

          .sticky-cart-price {
            font-size: 0.8125rem;
            opacity: 0.92;
          }

          .sticky-cart-button {
            width: 100%;
            min-width: 0;
            padding: 10px 14px;
            font-size: 0.8125rem;
            line-height: 1;
            margin-bottom: 0;
          }

          body.has-sticky-cart .back-to-top {
            bottom: calc(110px + env(safe-area-inset-bottom));
          }
        }

        @media screen and (max-width: 430px) {
          .sticky-cart-content {
            gap: 7px;
          }

          .sticky-cart-image {
            width: 36px;
            height: 36px;
          }

          .sticky-cart-title,
          .sticky-cart-price {
            font-size: 0.75rem;
          }

          .sticky-cart-button {
            width: 100%;
            padding: 9px 12px;
            font-size: 0.75rem;
          }

          body.has-sticky-cart .back-to-top {
            bottom: calc(104px + env(safe-area-inset-bottom));
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // ==========================================
  // PRODUCT IMAGE LIGHTBOX GALLERY
  // ==========================================
  class ProductImageGallery {
    constructor() {
      this.mainImage = document.querySelector('.product-single #productMainImage');
      this.thumbnailImages = Array.from(document.querySelectorAll('.product-single .product-thumbnail img'));
      if (!this.mainImage) return;

      this.images = this.getImages();
      this.currentIndex = 0;
      this.modal = null;
      this.modalImage = null;
      this.prevButton = null;
      this.nextButton = null;
      this.bodyOverflow = '';
      this.handleKeydown = this.handleKeydown.bind(this);

      this.init();
    }

    init() {
      this.mainImage.style.cursor = 'zoom-in';

      this.thumbnailImages.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => {
          this.currentIndex = index;
          this.updateMainImage(index);
        });
      });

      this.mainImage.addEventListener('click', () => {
        this.currentIndex = this.getCurrentIndex();
        this.open();
      });

      this.createModal();
      this.addStyles();
    }

    getImages() {
      if (this.thumbnailImages.length > 0) {
        return this.thumbnailImages.map((thumbnail) => ({
          main: thumbnail.dataset.mainImage || thumbnail.src,
          full: thumbnail.dataset.fullImage || thumbnail.dataset.mainImage || thumbnail.src,
          alt: thumbnail.alt || this.mainImage.alt || ''
        }));
      }

      return [{
        main: this.mainImage.src,
        full: this.mainImage.dataset.fullImage || this.mainImage.src,
        alt: this.mainImage.alt || ''
      }];
    }

    getCurrentIndex() {
      const currentSrc = this.mainImage.getAttribute('src');
      const matchedIndex = this.images.findIndex((image) => image.main === currentSrc);
      return matchedIndex >= 0 ? matchedIndex : this.currentIndex;
    }

    updateMainImage(index) {
      const image = this.images[index];
      if (!image) return;

      this.mainImage.src = image.main;
      this.mainImage.alt = image.alt;
      this.mainImage.dataset.fullImage = image.full;

      document.querySelectorAll('.product-single .product-thumbnail').forEach((thumbnail, thumbIndex) => {
        thumbnail.classList.toggle('active', thumbIndex === index);
      });
    }

    createModal() {
      this.modal = document.createElement('div');
      this.modal.className = 'product-gallery-modal';
      this.modal.innerHTML = `
        <button type="button" class="product-gallery-close" aria-label="Close gallery">&times;</button>
        <button type="button" class="product-gallery-nav product-gallery-prev" aria-label="Previous image">&#8249;</button>
        <img class="product-gallery-image" alt="">
        <button type="button" class="product-gallery-nav product-gallery-next" aria-label="Next image">&#8250;</button>
      `;

      document.body.appendChild(this.modal);
      this.modalImage = this.modal.querySelector('.product-gallery-image');
      this.prevButton = this.modal.querySelector('.product-gallery-prev');
      this.nextButton = this.modal.querySelector('.product-gallery-next');

      this.modal.querySelector('.product-gallery-close').addEventListener('click', () => this.close());
      this.prevButton.addEventListener('click', () => this.navigate(-1));
      this.nextButton.addEventListener('click', () => this.navigate(1));

      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.close();
      });
    }

    open() {
      this.renderModalImage();
      this.modal.classList.add('active');
      this.bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.handleKeydown);
    }

    close() {
      if (!this.modal.classList.contains('active')) return;
      this.modal.classList.remove('active');
      document.body.style.overflow = this.bodyOverflow;
      document.removeEventListener('keydown', this.handleKeydown);
    }

    navigate(direction) {
      const imageCount = this.images.length;
      if (imageCount <= 1) return;

      this.currentIndex = (this.currentIndex + direction + imageCount) % imageCount;
      this.updateMainImage(this.currentIndex);
      this.renderModalImage();
    }

    renderModalImage() {
      const image = this.images[this.currentIndex];
      if (!image) return;

      this.modalImage.src = image.full;
      this.modalImage.alt = image.alt;

      const hasMultipleImages = this.images.length > 1;
      this.prevButton.style.display = hasMultipleImages ? 'flex' : 'none';
      this.nextButton.style.display = hasMultipleImages ? 'flex' : 'none';
    }

    handleKeydown(event) {
      if (!this.modal.classList.contains('active')) return;

      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'ArrowLeft') {
        this.navigate(-1);
      } else if (event.key === 'ArrowRight') {
        this.navigate(1);
      }
    }

    addStyles() {
      const style = document.createElement('style');
      style.textContent = `
        .product-gallery-modal {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.92);
          z-index: 3000;
          display: none;
          align-items: center;
          justify-content: center;
          padding: 32px;
        }

        .product-gallery-modal.active {
          display: flex;
        }

        .product-gallery-image {
          max-width: min(90vw, 1400px);
          max-height: 88vh;
          width: auto;
          height: auto;
          object-fit: contain;
          border-radius: 8px;
        }

        .product-gallery-close {
          position: absolute;
          top: 18px;
          right: 18px;
          width: 44px;
          height: 44px;
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.5);
          color: #fff;
          font-size: 1.9rem;
          line-height: 1;
          cursor: pointer;
        }

        .product-gallery-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 48px;
          height: 48px;
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.5);
          color: #fff;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          line-height: 1;
          display: flex;
        }

        .product-gallery-prev {
          left: 20px;
        }

        .product-gallery-next {
          right: 20px;
        }

        @media screen and (max-width: 768px) {
          .product-gallery-modal {
            padding: 18px;
          }

          .product-gallery-nav {
            width: 42px;
            height: 42px;
            font-size: 1.75rem;
          }

          .product-gallery-prev {
            left: 8px;
          }

          .product-gallery-next {
            right: 8px;
          }

          .product-gallery-close {
            top: 10px;
            right: 10px;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // ==========================================
  // QUANTITY INPUT CONTROLS
  // ==========================================
  class QuantityControls {
    constructor() {
      this.init();
    }

    init() {
      document.querySelectorAll('.product-single .quantity-input').forEach(input => {
        if (input.closest('.quantity-stepper')) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'quantity-stepper';

        const minusButton = document.createElement('button');
        minusButton.type = 'button';
        minusButton.className = 'quantity-btn quantity-minus';
        minusButton.setAttribute('aria-label', 'Decrease quantity');
        minusButton.textContent = '−';

        const plusButton = document.createElement('button');
        plusButton.type = 'button';
        plusButton.className = 'quantity-btn quantity-plus';
        plusButton.setAttribute('aria-label', 'Increase quantity');
        plusButton.textContent = '+';

        const parent = input.parentElement;
        parent.insertBefore(wrapper, input);
        wrapper.appendChild(minusButton);
        wrapper.appendChild(input);
        wrapper.appendChild(plusButton);

        minusButton.addEventListener('click', () => {
          const val = parseInt(input.value) || 1;
          if (val > 1) input.value = val - 1;
        });

        plusButton.addEventListener('click', () => {
          const val = parseInt(input.value) || 1;
          input.value = val + 1;
        });
      });

      this.addStyles();
    }

    addStyles() {
      const style = document.createElement('style');
      style.textContent = `
        .quantity-stepper {
          display: inline-flex;
          align-items: stretch;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          overflow: hidden;
          background-color: var(--color-secondary);
        }

        .quantity-btn {
          width: 40px;
          min-height: 46px;
          border: 0;
          background-color: transparent;
          color: var(--color-text);
          cursor: pointer;
          font-size: 1.4rem;
          line-height: 1;
          transition: background-color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quantity-btn:hover {
          background-color: rgba(255, 255, 255, 0.08);
        }

        .quantity-minus {
          border-right: 1px solid var(--color-border);
        }

        .quantity-plus {
          border-left: 1px solid var(--color-border);
        }

        .quantity-stepper .quantity-input {
          width: 62px;
          min-height: 46px;
          border: 0;
          border-radius: 0;
          padding: 0;
          text-align: center;
          background: transparent;
          color: var(--color-text);
          -moz-appearance: textfield;
        }

        .quantity-stepper .quantity-input::-webkit-outer-spin-button,
        .quantity-stepper .quantity-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .quantity-stepper .quantity-input:focus {
          outline: none;
        }

        @media screen and (max-width: 768px) {
          .quantity-btn {
            width: 38px;
            min-height: 44px;
          }

          .quantity-stepper .quantity-input {
            width: 56px;
            min-height: 44px;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // ==========================================
  // LAZY LOAD IMAGES
  // ==========================================
  function lazyLoadImages() {
    if ('IntersectionObserver' in window) {
      const images = document.querySelectorAll('img[loading="lazy"]');
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src || img.src;
            imageObserver.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    }
  }

  // ==========================================
  // PRODUCT QUICK VIEW (Modal)
  // ==========================================
  function initQuickView() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('.product-card-quick-view')) {
        e.preventDefault();
        // Quick view modal would be implemented here
        console.log('Quick view clicked');
      }
    });
  }

  // ==========================================
  // INITIALIZE ALL FEATURES
  // ==========================================
  function init() {
    // Apply animations to product cards before reveal observer initializes.
    document.querySelectorAll('.product-card').forEach((card, index) => {
      card.classList.add('scroll-reveal');
      card.style.transitionDelay = `${index * 0.05}s`;
    });

    // Initialize features
    new ScrollReveal();
    new ParallaxEffect();
    new ScrollProgress();
    new BackToTop();
    new StickyAddToCart();
    new ProductImageGallery();
    new QuantityControls();
    
    lazyLoadImages();
    initQuickView();

    // Track recently viewed product
    const productId = document.querySelector('[data-product-id]')?.dataset.productId;
    if (productId && document.querySelector('.product-single')) {
      const recentlyViewed = new RecentlyViewed();
      recentlyViewed.add({
        id: productId,
        title: document.querySelector('.product-title')?.textContent || '',
        price: document.querySelector('.product-price')?.textContent || '',
        image: document.querySelector('.product-main-image img')?.src || '',
        url: window.location.pathname
      });
    }

  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose RecentlyViewed globally
  window.RecentlyViewed = RecentlyViewed;

})();
